import React, { useState } from 'react';
import { Icon } from './icons';
import { money } from './format';
import { Modal } from './sheets';
import { Segmented } from './shared';
import { seriesKeyOf, legacySeriesKeyOf, activePlans, plannedNext, isEnded, lifecycleOf, missedCharges } from '../lib/forecasting';
import { formatMerchantName } from '../lib/merchant';
import { seriesDisplayName } from './dataAdapters';

const CADENCE_LABEL = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', semiannual: 'Semi-annual', yearly: 'Yearly' };

function RecurringSuggestions({ candidates, onConfirm, onDismiss }) {
  if (!candidates || candidates.length === 0) return null;
  return (
    <div className="pg-section">
      <div className="pg-head">
        <div className="eyebrow">
          {candidates.length} pattern{candidates.length === 1 ? '' : 's'} detected
        </div>
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>confirm and future matches are marked automatically</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {candidates.map((c) => (
          <div key={c.seriesKey} className="row-line" style={{ alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatMerchantName(c.description) || c.description}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                {CADENCE_LABEL[c.recurrenceType]} · {money(Number(c.amount))} {c.type === 'income' ? 'in' : 'out'} · seen {c.count}&times;{c.variable && ' · varies'}
              </div>
            </div>
            <button type="button" onClick={() => onConfirm(c)} className="chip pressable">Confirm</button>
            <button type="button" onClick={() => onDismiss(c.seriesKey)} aria-label="Dismiss this pattern" className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
              <Icon name="plus" size={15} style={{ transform: 'rotate(45deg)' }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditSeriesForm({ series, categories, onSave, onClose }) {
  const [displayName, setDisplayName] = useState(series.displayName);
  const [amount, setAmt] = useState(series.amount);
  const [category, setCat] = useState(series.category);
  const [type, setType] = useState(series.type);
  const [recurrenceType, setRecType] = useState(series.recurrenceType);

  const handleSave = () => {
    if (!displayName || !amount) return;
    onSave({ displayName, amount: parseFloat(amount), category, type, recurrenceType });
  };

  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
        Updates all {series.instances.length} logged instances
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Segmented
          options={[{ value: 'expense', label: 'Expense' }, { value: 'income', label: 'Income' }]}
          value={type}
          onChange={setType}
        />
        <div>
          <input className="field" type="text" placeholder="Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, paddingLeft: 2 }}>
            Display name only — new charges matching “{series.description}” will still be added to this series.
          </p>
        </div>
        <input className="field" type="number" placeholder="Amount" value={amount} onChange={(e) => setAmt(e.target.value)} step="0.01" />
        <select className="field" value={category} onChange={(e) => setCat(e.target.value)}>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select className="field" value={recurrenceType} onChange={(e) => setRecType(e.target.value)}>
          {['daily', 'weekly', 'biweekly', 'monthly', 'semiannual', 'yearly'].map((r) => (
            <option key={r} value={r}>{r === 'semiannual' ? 'Semi-annual' : r.charAt(0).toUpperCase() + r.slice(1)}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={onClose} className="btn-quiet is-block pressable">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary is-block pressable" style={{ flex: 2 }}>
            Save all instances
          </button>
        </div>
      </div>
    </>
  );
}

const CADENCES = ['daily', 'weekly', 'biweekly', 'monthly', 'semiannual', 'yearly'];
const todayInputDate = () => new Date().toLocaleDateString('en-CA');
const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// A recurring commitment entered before its first charge: a new lease, a
// subscription starting next month. It's forecast straight away and joins a
// real series once a matching transaction arrives.
function PlannedForm({ plan, categories = [], accounts = [], onSave, onClose }) {
  const [type, setType] = useState(plan?.type || 'expense');
  const [name, setName] = useState(plan?.name || '');
  const [amount, setAmount] = useState(plan?.amount ? String(plan.amount) : '');
  const [recurrenceType, setRecurrenceType] = useState(plan?.recurrenceType || 'monthly');
  const [firstDate, setFirstDate] = useState(plan?.firstDate || todayInputDate());
  const [category, setCategory] = useState(plan?.category || (categories.includes('Other') ? 'Other' : categories[0] || 'Other'));
  const [accountId, setAccountId] = useState(plan?.accountId || '');
  const [matchHint, setMatchHint] = useState(plan?.matchHint || '');

  const valid = name.trim() && Number(amount) > 0 && firstDate;
  const submit = () => {
    if (!valid) return;
    onSave({
      id: plan?.id,
      type,
      name: name.trim(),
      amount: Math.abs(parseFloat(amount)),
      recurrenceType,
      firstDate,
      category,
      accountId: accountId || null,
      matchHint: matchHint.trim() || null,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
        Counted in your forecast and safe-to-spend from now on. It won&rsquo;t appear as a transaction — the first real charge that matches joins it to a series.
      </p>
      <Segmented
        options={[{ value: 'expense', label: 'Expense' }, { value: 'income', label: 'Income' }]}
        value={type}
        onChange={setType}
      />
      <input className="field" type="text" placeholder="Name, e.g. Gym membership" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="field" type="number" placeholder="Amount" aria-label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} step="0.01" min="0" />
      <div style={{ display: 'flex', gap: 10 }}>
        <label style={{ flex: 1 }}>
          <span className="field-label">Repeats</span>
          <select className="field" value={recurrenceType} onChange={(e) => setRecurrenceType(e.target.value)}>
            {CADENCES.map((r) => <option key={r} value={r}>{CADENCE_LABEL[r]}</option>)}
          </select>
        </label>
        <label style={{ flex: 1 }}>
          <span className="field-label">First charge</span>
          <input className="field" type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)} />
        </label>
      </div>
      <label>
        <span className="field-label">Category</span>
        <select className="field" value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </label>
      {accounts.length > 0 && (
        <label>
          <span className="field-label">{type === 'income' ? 'Paid into' : 'Paid from'}</span>
          <select className="field" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Any account</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.lastFour ? ` ··${a.lastFour}` : ''}</option>)}
          </select>
        </label>
      )}
      <label>
        <span className="field-label">Shows up as (optional)</span>
        <input className="field" type="text" placeholder="e.g. PLANET FIT" value={matchHint} onChange={(e) => setMatchHint(e.target.value)} />
        <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginTop: 6, paddingLeft: 2 }}>
          Part of the bank&rsquo;s description, if you know it — helps match the first charge.
        </span>
      </label>
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button type="button" onClick={onClose} className="btn-quiet is-block pressable" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
        <button type="button" onClick={submit} disabled={!valid} className="btn-primary is-block pressable" style={{ flex: 2, justifyContent: 'center' }}>
          {plan?.id ? 'Save' : 'Add recurring'}
        </button>
      </div>
    </div>
  );
}

const monthlyMult = { daily: 30, weekly: 4, biweekly: 2, monthly: 1, semiannual: 1 / 6, yearly: 1 / 12 };

function advanceOnePeriod(date, recurrenceType) {
  const next = new Date(date);
  if (recurrenceType === 'daily') next.setDate(next.getDate() + 1);
  if (recurrenceType === 'weekly') next.setDate(next.getDate() + 7);
  if (recurrenceType === 'biweekly') next.setDate(next.getDate() + 14);
  if (recurrenceType === 'monthly') next.setMonth(next.getMonth() + 1);
  if (recurrenceType === 'semiannual') next.setMonth(next.getMonth() + 6);
  if (recurrenceType === 'yearly') next.setFullYear(next.getFullYear() + 1);
  return next;
}

function getNextDate(instances, recurrenceType) {
  const sorted = [...instances].sort((a, b) => new Date(b.date) - new Date(a.date));
  const last = new Date(sorted[0].date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = advanceOnePeriod(last, recurrenceType);
  // Roll forward until the next occurrence is today or later.
  let safety = 0;
  while (next < today && safety < 10000) {
    next = advanceOnePeriod(next, recurrenceType);
    safety++;
  }
  return next;
}

export function RecurringView({
  transactions,
  seriesNames = {},
  categories,
  onDeleteSeries,
  onEditSeries,
  suggestions = [],
  onConfirmSuggestion,
  onDismissSuggestion,
  onGoToTransactions,
  planned = [],
  accounts = [],
  onSavePlanned,
  onDeletePlanned,
  planSuggestions = [],
  onLinkPlan,
  onDismissPlanMatch,
  lifecycle = {},
  missed = [],
  missedCandidates = [],
  onLinkMissed,
  onSkipMissed,
  onEndSeries,
  onResumeSeries,
  onDismissMissedMatch,
  desktop,
}) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [editSeries, setEditSeries] = useState(null);
  // null = closed; {} = adding; a plan = editing it.
  const [planForm, setPlanForm] = useState(null);

  const plans = activePlans(planned)
    .map((p) => ({ ...p, ...plannedNext(p) }))
    .sort((a, b) => a.next - b.next);
  const suggestionsByPlan = {};
  planSuggestions.forEach((s) => { (suggestionsByPlan[s.plan.id] ||= []).push(s); });
  const missedByKey = {};
  missedCandidates.forEach((c) => { (missedByKey[c.key] ||= []).push(c); });

  const seriesMap = {};
  transactions.filter((t) => t.isRecurring).forEach((t) => {
    const key = seriesKeyOf(t);
    if (!seriesMap[key]) {
      // displayName is the legible form (custom rename > normalized merchant);
      // description stays raw — series keys and bank matching derive from it.
      seriesMap[key] = { key, description: t.description, displayName: seriesDisplayName(key, t.description, seriesNames, legacySeriesKeyOf(t)), type: t.type, category: t.category, recurrenceType: t.recurrenceType, instances: [] };
    }
    seriesMap[key].instances.push(t);
  });

  const series = Object.values(seriesMap)
    .map((s) => {
      const amts = s.instances.map((t) => Number(t.amount) || 0);
      const amount = amts.reduce((a, b) => a + b, 0) / amts.length;
      const amtVaries = Math.max(...amts) - Math.min(...amts) > 0.01;
      const logoUrl = s.instances.find((t) => t.logoUrl)?.logoUrl || null;
      const website = s.instances.find((t) => t.website)?.website || null;
      const lastDate = new Date(Math.max(...s.instances.map((t) => new Date(t.date).getTime())));
      const ended = isEnded(lifecycleOf(lifecycle, s.key), lastDate);
      return { ...s, amount, amtVaries, logoUrl, website, lastDate, ended };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const nameByKey = Object.fromEntries(series.map((s) => [s.key, s.displayName]));
  // A charge that's due but hasn't posted is still the next one — the same
  // date Today counts it on, not the cycle after.
  const overdueByKey = Object.fromEntries(missedCharges(transactions, lifecycle).map((m) => [m.key, m]));

  // Planned series count toward the monthly figure: they're commitments the
  // user has told us about, the same thing the figure claims to total.
  // An ended series is no longer a commitment.
  const committed = [...series.filter((s) => !s.ended), ...plans.map((p) => ({ type: p.type, amount: p.amount, recurrenceType: p.recurrenceType }))];
  const monthlyIn = committed.filter((s) => s.type === 'income').reduce((sum, s) => sum + s.amount * (monthlyMult[s.recurrenceType] || 1), 0);
  const monthlyOut = committed.filter((s) => s.type === 'expense').reduce((sum, s) => sum + s.amount * (monthlyMult[s.recurrenceType] || 1), 0);

  const addButton = onSavePlanned && (
    <button type="button" onClick={() => setPlanForm({})} className="btn-quiet pressable">
      <Icon name="plus" size={14} sw={2.2} />
      Add recurring
    </button>
  );

  const planModal = (
    <Modal open={!!planForm} onClose={() => setPlanForm(null)} title={planForm?.id ? 'Edit planned series' : 'Add recurring'}>
      {planForm && (
        <PlannedForm
          plan={planForm.id ? planForm : null}
          categories={categories}
          accounts={accounts}
          onSave={(p) => { onSavePlanned(p); setPlanForm(null); }}
          onClose={() => setPlanForm(null)}
        />
      )}
    </Modal>
  );

  if (series.length === 0 && plans.length === 0) {
    return (
      <div className="pg">
        <RecurringSuggestions candidates={suggestions} onConfirm={onConfirmSuggestion} onDismiss={onDismissSuggestion} />
        <div className="pg-section">
          <p className="pg-note">
            {suggestions.length > 0
              ? 'No confirmed series yet. Confirm one of the patterns above, add one you know is coming, or open a transaction and check "Recurring".'
              : 'No recurring transactions yet. Add one you know is coming, or open any transaction and check "Recurring" \u2014 a pattern is also detected on its own once a bill repeats a few times.'}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {addButton}
            {onGoToTransactions && (
              <button type="button" onClick={onGoToTransactions} className="btn-primary pressable">
                <Icon name="list" size={15} sw={2.2} />
                Go to Transactions
              </button>
            )}
          </div>
        </div>
        {planModal}
      </div>
    );
  }

  const net = monthlyIn - monthlyOut;

  return (
    <div className="pg">
      <RecurringSuggestions candidates={suggestions} onConfirm={onConfirmSuggestion} onDismiss={onDismissSuggestion} />

      {/* One figure and its sentence, the same shape Today and Budget use —
          two equal stat cards gave neither number a job. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
        <div className="pg-figure" style={{ color: net < 0 ? 'var(--alarm)' : 'var(--ink)' }}>
          {money(net, { cents: false, sign: true })}
        </div>
        <div className="pg-lede">
          a month once every commitment is counted — <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{money(monthlyIn, { cents: false })}</b> in
          against <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{money(monthlyOut, { cents: false })}</b> out
        </div>
      </div>

      {/* Series whose charge is overdue. Each needs one answer: link the charge
          that did arrive under another name, skip this cycle, or end it. */}
      {missed.length > 0 && onSkipMissed && (
        <div className="pg-section">
          <div className="pg-head">
            <div className="eyebrow">Haven&rsquo;t seen</div>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>still counted as due until you say otherwise</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {missed.map((m) => {
              const name = nameByKey[m.key] || formatMerchantName(m.description) || m.description;
              return (
                <React.Fragment key={m.key}>
                  <div className="row-line" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                          {CADENCE_LABEL[m.recurrenceType] || m.recurrenceType} · expected {shortDate(m.expectedDate)} · not seen yet
                        </div>
                      </div>
                      <div className="num" style={{ flexShrink: 0, fontWeight: 600, fontSize: 14.5, color: 'var(--ink)' }}>
                        {money(m.type === 'income' ? m.amount : -m.amount, { sign: true })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => onSkipMissed(m)} aria-label={`${name} was skipped this time`} className="chip pressable">Skipped this time</button>
                      <button type="button" onClick={() => onEndSeries(m)} aria-label={`${name} has ended`} className="chip pressable">It ended</button>
                    </div>
                  </div>
                  {onLinkMissed && (missedByKey[m.key] || []).map((c) => (
                    <div key={c.tx.id} className="row-line" style={{ alignItems: 'center', paddingLeft: 14 }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Is this it? <b style={{ color: 'var(--ink)', fontWeight: 500 }}>{formatMerchantName(c.tx.description) || c.tx.description}</b>
                        {' · '}{shortDate(new Date(c.tx.date))} · <span className="num">{money(Number(c.tx.amount))}</span>
                      </div>
                      <button type="button" onClick={() => onLinkMissed(m, c.tx)} className="chip pressable" aria-label={`Link ${c.tx.description} to ${name}`}>
                        This is it
                      </button>
                      <button type="button" onClick={() => onDismissMissedMatch(m.key, c.tx.id)} aria-label={`${c.tx.description} isn't ${name}`} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                        <Icon name="plus" size={15} style={{ transform: 'rotate(45deg)' }} />
                      </button>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {plans.length > 0 && (
        <div className="pg-section">
          <div className="pg-head">
            <div className="eyebrow">Planned</div>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>forecast until the first charge arrives</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {plans.map((p) => {
              const deleteKey = `plan:${p.id}`;
              return (
                <React.Fragment key={p.id}>
                <div className="row-line" style={{ alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                      {CADENCE_LABEL[p.recurrenceType] || p.recurrenceType} · {p.overdue ? `expected ${shortDate(p.next)} · not seen yet` : `first ${shortDate(p.next)}`}
                    </div>
                  </div>
                  <div className="num" style={{ flexShrink: 0, fontWeight: 600, fontSize: 14.5, color: 'var(--ink)' }}>
                    {money(p.type === 'income' ? p.amount : -p.amount, { sign: true })}
                  </div>
                  {pendingDelete === deleteKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        aria-label={`Confirm deleting the planned ${p.name}`}
                        onClick={() => { onDeletePlanned(p.id); setPendingDelete(null); }}
                        className="pressable"
                        style={{ height: 30, padding: '0 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--bg)', background: 'var(--neg)' }}
                      >
                        Delete
                      </button>
                      <button type="button" aria-label="Cancel delete" onClick={() => setPendingDelete(null)} className="chip pressable">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="row-actions">
                      <button type="button" onClick={() => setPlanForm(p)} aria-label={`Edit the planned ${p.name}`} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                        <Icon name="edit" size={14} />
                      </button>
                      <button type="button" aria-label={`Delete the planned ${p.name}`} onClick={() => setPendingDelete(deleteKey)} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {/* Charges that fit on amount and date but couldn't link on
                    their own — the user says which, if any, it was. */}
                {onLinkPlan && (suggestionsByPlan[p.id] || []).map((s) => (
                  <div key={s.tx.id} className="row-line" style={{ alignItems: 'center', paddingLeft: 14 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Is this it? <b style={{ color: 'var(--ink)', fontWeight: 500 }}>{formatMerchantName(s.tx.description) || s.tx.description}</b>
                      {' · '}{shortDate(new Date(s.tx.date))} · <span className="num">{money(Number(s.tx.amount))}</span>
                    </div>
                    <button type="button" onClick={() => onLinkPlan(p, s.tx)} className="chip pressable" aria-label={`Link ${s.tx.description} to ${p.name}`}>
                      This is it
                    </button>
                    <button type="button" onClick={() => onDismissPlanMatch(p.id, s.tx.id)} aria-label={`${s.tx.description} isn't ${p.name}`} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                      <Icon name="plus" size={15} style={{ transform: 'rotate(45deg)' }} />
                    </button>
                  </div>
                ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="pg-section">
        <div className="pg-head">
          <div className="eyebrow">Recurring series</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="num" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{series.length}</span>
            {addButton}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {series.map((s) => {
            const nextDate = getNextDate(s.instances, s.recurrenceType);
            const isPending = pendingDelete === s.key;
            return (
              <div key={s.key} className="row-line" style={{ alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.displayName}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                    {CADENCE_LABEL[s.recurrenceType] || s.recurrenceType} · {s.ended
                      ? `ended · last ${shortDate(s.lastDate)}`
                      : overdueByKey[s.key]
                        ? `expected ${shortDate(overdueByKey[s.key].expectedDate)} · not seen yet`
                        : `next ${shortDate(nextDate)}`} · {s.instances.length} logged
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div className="num" style={{ fontWeight: 600, fontSize: 14.5, color: 'var(--ink)' }}>
                    {money(s.type === 'income' ? s.amount : -s.amount, { sign: true })}
                  </div>
                  {/* Under the amount, not a ~ in front of it: the figure is the
                      average, and a ~ crowded the minus sign beside it. */}
                  {s.amtVaries && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>varies</div>
                  )}
                </div>
                {s.ended && onResumeSeries && !isPending && (
                  <button type="button" onClick={() => onResumeSeries(s.key)} aria-label={`Resume the ${s.displayName} series`} className="chip pressable">
                    Resume
                  </button>
                )}
                {isPending ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      type="button"
                      aria-label={`Confirm deleting the ${s.displayName} series`}
                      onClick={() => { onDeleteSeries(s.instances.map((inst) => inst.id)); setPendingDelete(null); }}
                      className="pressable"
                      style={{ height: 30, padding: '0 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--bg)', background: 'var(--neg)' }}
                    >
                      Delete
                    </button>
                    <button type="button" aria-label="Cancel delete" onClick={() => setPendingDelete(null)} className="chip pressable">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="row-actions">
                    <button type="button" onClick={() => setEditSeries(s)} aria-label={`Edit the ${s.displayName} series`} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                      <Icon name="edit" size={14} />
                    </button>
                    <button type="button" aria-label={`Delete the ${s.displayName} series`} onClick={() => setPendingDelete(s.key)} className="chip pressable" style={{ width: 30, padding: 0, justifyContent: 'center' }}>
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={!!editSeries} onClose={() => setEditSeries(null)} title="Edit series">
        {editSeries && (
          <EditSeriesForm
            series={editSeries}
            categories={categories}
            onSave={(updated) => { onEditSeries(editSeries.instances, updated); setEditSeries(null); }}
            onClose={() => setEditSeries(null)}
          />
        )}
      </Modal>
      {planModal}
    </div>
  );
}
